/* 
  Provides a simple way of making API requests (using fetch api) while integrating the following features:
  - The ability to serve up mock data
  - Custom data transformations
  - Automatic spinners during requests
  - Automatic error notifications
  - Automatic endpoint path resolution
  - Default headers
*/

import { Notify } from 'quasar'
import { ApolloClient } from '@/vue-apollo'
import gql from 'graphql-tag'

// Default config which can be overriden by using apiHelper.config({})
let config = {
  useMocks: process.env.NODE_ENV === 'development',
  mockResponseTime: 500
}
let incompleteRequests = 0 // Tracks number of requests currently running in order to know when to hide the spinner (for requests that are using automatic spinner).
let $store // Access to Vuex store, as passed in via config() method.

export default {
  /*
    Allows the initial setting of API configuration parameters below. Ideally this should be set from main.js.
    - store <object>: The store should be passed in so that state can be accessed if necessary.
    - apiRoot <string>: Default <empty>. If using a primary api, its root should be specified here (e.g. https://test.api/v1). Then individual requests can use relative endpoints.
    - useMocks <boolean>: Default <true> in development environment. Determines if mock responses will be returned when requests specify them.
    - mockResponseTime <integer>: Default 500. Number of milliseconds delay between the request and the mock response. This can be useful for making the mock experience more realistic, giving a chance to see things like spinners and "pop-in".
    - headers <json>: Optional default headers. Note that any headers specified in the request will take precedence.
    - onShowSpinner(message) {} <function>: Callback is fired when a request starts.
        - message <string>: A message that can accompany the spinner, e.g. "Updating..."
    - onHideSpinner() {} <function>: Callback is fired when all outstanding requests have completed.
  */
  config(data) {
    $store = data.store
    config = {
      apiRoot: data.apiRoot || config.apiRoot,
      useMocks: data.useMocks !== undefined ? data.useMocks : config.useMocks,
      mockResponseTime:
        data.mockResponseTime >= 0
          ? data.mockResponseTime
          : config.mockResponseTime,
      headers: data.headers || config.headers,
      onShowSpinner: data.onShowSpinner,
      onHideSpinner: data.onHideSpinner
    }
  },

  get(endpoint, params) {
    /* 
      Makes a GET request for a specified endpoint.
      - endpoint <string>:  Required endpoint, e.g. "applications/<id>". Will be prefixed with API url unless endpoint is already a URL ("http...")
      - params <object>: Optional
        - headers <object>:  Optional headers to use instead of defaults
        - mock <string>:  Optional filename (without ".js") of mock data within api/mock-responses folder. If specified, mock data will be returned as a promise when running localhost.
        - transformation(data) <function>:  Optional data transformation. With GET requests, transformation is performed on "response.data".
        - spinner <boolean>:  Optional flag to specify if spinner should appear during the request (providing onShowSpinner/onHideSpinner callbacks have been set). Default is "true".
    */
    params = params || {}
    params.method = 'get'
    params.endpoint = endpoint
    return request(params)
  },

  post(endpoint, params) {
    /* 
      Makes a POST request for a specified endpoint.
      - endpoint <string>:  Required endpoint, e.g. "applications/<id>". Will be prefixed with API url unless endpoint is already a URL ("http...")
      - params <object>: Optional
        - data <json>:  Optional payload data
        - headers <object>:  Optional headers to use instead of defaults
        - mock <string>:  Optional filename (without ".js") of mock data within api/mock-responses folder. If specified, mock data will be returned as a promise when running localhost.
        - transformation(data) <function>:  Optional data transformation. With POST/PUT requests, transformation is performed on payload.
        - spinner <boolean>:  Optional flag to specify if spinner should appear during the request (providing onShowSpinner/onHideSpinner callbacks have been set). Default is "true".
    */
    params = params || {}
    params.method = 'post'
    params.endpoint = endpoint
    return request(params)
  },

  put(endpoint, params) {
    /* 
      Makes a PUT request for a specified endpoint.
      - endpoint <string>:  Required endpoint, e.g. "applications/<id>". Will be prefixed with API url unless endpoint is already a URL ("http...")
      - params <object>: Optional
        - data <json>:  Optional payload data
        - headers <object>:  Optional headers to use instead of defaults
        - mock <string>:  Optional filename (without ".js") of mock data within api/mock-responses folder. If specified, mock data will be returned as a promise when running localhost.
        - transformation(data) <function>:  Optional data transformation. With POST/PUT requests, transformation is performed on payload.
        - spinner <boolean>:  Optional flag to specify if spinner should appear during the request (providing onShowSpinner/onHideSpinner callbacks have been set). Default is "true".
    */
    params = params || {}
    params.method = 'put'
    params.endpoint = endpoint
    return request(params)
  },

  query(query, variables, params) {
    /* 
      Makes a GraphQL query request.
      - query <string>:  Query string
      - variables <json>: Optional variables
      - params <object>: Optional
        - transformation(data) <function>:  Data transformation to be performed on response data.
        - spinner <boolean>:  If true, spinner will automatically be displayed while request is running. Default is "true".
    */
    params = params || {}
    params.method = 'query'
    params.query = query
    params.variables = variables
    return gqlRequest(params)
  },

  mutate(mutation, variables, params) {
    /* 
      Makes a GraphQL mutate request.
      - mutation <string>:  Query string
      - variables <json>: Optional variables
      - params <object>: Optional
        - transformation(data) <function>:  Data transformation to be performed on response data.
        - spinner <boolean>:  If true, spinner will automatically be displayed while request is running. Default is "true".
    */
    params = params || {}
    params.method = 'mutate'
    params.query = mutation
    params.variables = variables
    return gqlRequest(params)
  }
}

// Local helper functions ---------------------------------------------

async function request({
  method,
  endpoint,
  data,
  spinner = true,
  headers,
  mock,
  transformation
}) {
  /* 
    Makes an API request using FetchAPI
      method <string> - Required: These methods are currently supported: get | post | put
      endpoint <string> - Required: e.g. "/applications/<id>". Will be prefixed with API url unless endpoint is already a URL ("http...")
      data <json> - Optional payload data for FetchAPI
      spinner <boolean> - Optional flag to specify if spinner should appear during the request. Default is "true".
      headers <object> - Optional FetchAPI headers to use instead of defaults
      mock <string> - Optional filename of mock data within api/mock-responses folder. If specified, mock data will be returned when running localhost.
      transformation <function> - Optional data transformation. With GET requests, transformation is performed on "response.data". With POST/PUT requests, transformation is performed on payload.
  */

  let isUpdating = method !== 'get'
  showSpinner(spinner, isUpdating)

  // If endpoint has mock data available and running localhost then return mock data...
  let mockData = getMockData(mock, method, transformation)
  if (mockData) {
    return await new Promise(resolve =>
      setTimeout(() => {
        hideSpinner(spinner)
        resolve(mockData)
      }, config.mockResponseTime)
    )
  }

  // Otherwise make real api request...
  const request = new Request(resolveEndpoint(endpoint), {
    method: method.toUpperCase(),
    body: getPayload(data, transformation),
    headers: new Headers(headers || getHeaders())
  })

  try {
    const response = await fetch(request)
    const responseData = await response.json()
    hideSpinner(spinner)
    if (response.status === 200) {
      if (is200error(responseData, method)) {
        return null
      }

      if (method === 'get') {
        // For GET requests, return response data with any transformation applied.
        return transformation ? transformation(responseData) : responseData
      } else {
        // For PUT/POST/DELETE requests, return response object.
        return responseData
      }
    } else {
      throw new Error(responseData.message)
    }
  } catch (e) {
    hideSpinner(spinner)
    notifyError(method, e.message)
    throw new Error('Something went wrong on api server!')
  }
}

async function gqlRequest({
  method,
  query,
  variables,
  spinner = true,
  transformation
}) {
  /* 
    Makes a GraphQL request and returns response data. Note that this is not currently
    set up for a query that has multiple tables. Only the data for the first table will
    be returned.
      method <string> - Required GraphQL method - "query | mutate"  
      query <string> - GraphQL request
      variables <json> - Optional variables for GraphQL request
      spinner <boolean> - Optional flag to specify if spinner should appear during the request. Default is "true".
      transformation <function> - Optional data transformation on response data. Takes data parameter and must return transformed data.
  */

  showSpinner(spinner, method === 'mutate')

  try {
    const response = await ApolloClient.query({
      query: gql(query),
      variables: variables
    })
    const dataKey = Object.keys(response.data)[0]
    const responseData = response.data[dataKey]
    hideSpinner(spinner)
    try {
      return transformation ? transformation(responseData) : responseData
    } catch (e) {
      notifyError(null, 'Query response data transformation failed')
      return responseData
    }
  } catch (e) {
    hideSpinner(spinner)
    notifyError(method, e.message)
    throw new Error('Something went wrong on api server!')
  }
}

function getHeaders() {
  let headers = {
    'Content-Type': 'application/json'
  }
  if ($store.getters.isAuthenticated) {
    headers.Authorization = 'Bearer ' + $store.getters.getAuthToken
  }
  return headers
}

function is200error(responseData, method) {
  // When the request is successful (200) we still need to detect if the server has
  // returned an error response.
  if (
    responseData &&
    responseData.error &&
    Object.keys(responseData).length <= 2
  ) {
    notifyError(method, responseData.message || responseData.error)
    return true
  }
}

function getPayload(data, transformation) {
  let payload = transformation && data ? transformation(data) : data
  return JSON.stringify(payload)
}

function resolveEndpoint(endpoint) {
  /*
    Endpoints can be full or relative. If relative, they will automatically
    be prefixed with the API root. The opening slash is optional for
    relative endpoints. 
  */
  if (
    endpoint.indexOf('http') === 0 ||
    endpoint.indexOf('https') === 0 ||
    endpoint.indexOf('//') === 0
  ) {
    return endpoint
  }
  if (endpoint.indexOf('/') !== 0) {
    endpoint = `/${endpoint}`
  }
  return config.apiRoot + endpoint
}

function notifyError(method, message) {
  let isUpdate = method !== 'get' || method === 'mutate'
  if (method) {
    message = `Could not ${isUpdate ? 'send' : 'load'} data: ${message}`
  }
  Notify.create({
    type: 'negative',
    message: message,
    timeout: method !== 'get' ? 10000 : 5000 // Failed posts are more serious than failed gets, so show message longer
  })
}

function showSpinner(isRequestUsingSpinner, isUpdating) {
  if (isRequestUsingSpinner) {
    incompleteRequests++
    if (typeof config.onShowSpinner === 'function') {
      config.onShowSpinner(isUpdating ? 'Updating...' : 'Loading...')
    }
  }
}

function hideSpinner(isRequestUsingSpinner) {
  /*
    Unless turned off, each request shows a spinner until complete. However each request
    can't just hide the spinner when complete because there could be other incomplete requests
    still running that need the spinner. So a counter is used to keep track of incomplete requests,
    and the spinner is only hidden after all requests have completed (incompleteRequests = 0)
  */
  if (isRequestUsingSpinner) {
    if (incompleteRequests) {
      incompleteRequests--
    }
    if (!incompleteRequests && typeof config.onHideSpinner === 'function') {
      config.onHideSpinner()
    }
  }
}

function getMockData(mockDataFilename, httpMethod, transformation) {
  /*
    When running on localhost, this returns the specified mock data. On production
    environments no mock data is returned or even imported ("require" allows us to
    conditionally import).

      mockDataFilename <string> - Specifies the file to import from "api/mock-responses" (no ".js" at the end)
      httpMethod <string> - API request type, e.g. 'get'
      transformation <function> - Optional api data transformation function
  */
  try {
    if (config.useMocks && mockDataFilename) {
      let mockData = require(`./mock-responses/${mockDataFilename}`).default
      if (transformation && httpMethod === 'get') {
        /*
            Only perform transformation for GET requests. In the rare event that we are mocking
            a PUT/POST response, it is the payload that should be transformed, not the response.
            The payload transformation is done elsewhere.
          */
        mockData = transformation(mockData)
      }
      return JSON.parse(JSON.stringify(mockData)) // Return clone of mock data, otherwise it can be mutated.
    }
  } catch (e) {
    return null
  }
}
